import { ChangeDetectorRef, Component, OnDestroy, OnInit, TemplateRef, ChangeDetectionStrategy, effect, inject, viewChild, input, linkedSignal, untracked } from '@angular/core';
import {ActivatedRoute} from '@angular/router';
import {Observable, Subscription} from 'rxjs';
import {ToastrService} from 'ngx-toastr';
import moment from 'moment-timezone';
import {SbModalService} from '@app/_components/unified/sb-modal/sb-modal.service';
import {SbModalButton} from '@app/_components/unified/sb-modal/sb-modal.types';
import {
  MonitoringCheckRow,
  MonitoringCheckRule,
  MonitoringCloseReason,
  MonitoringEventRow,
  MonitoringHistoryPage,
  MonitoringHistoryType,
  MonitoringIssue,
  MonitoringIssueHistoryFilters,
  MonitoringIssueHistoryRow,
  MonitoringIssueLevel,
  MonitoringIssueState,
  MonitoringLevel,
  MonitoringMetrics,
  MonitoringMute,
  MonitoringResolution,
  MonitoringResponse,
  MonitoringSite,
  MonitoringState,
  ProjectMonitoringData,
  ProjectMonitoringService
} from '@app/services/project-monitoring.service';
import { PageShellComponent } from '../../../_components/page-shell/page-shell.component';
import { SbButtonComponent } from '../../../_components/unified/sb-button/sb-button.component';
import { NgTemplateOutlet } from '@angular/common';
import { SbInputTextComponent } from '../../../_components/unified/sb-input/sb-input-text.component';
import { ReactiveFormsModule, FormsModule } from '@angular/forms';
import { SbTabsComponent } from '../../../_components/unified/sb-tabs/sb-tabs.component';
import { SbTabDirective } from '../../../_components/unified/sb-tabs/sb-tab.directive';
import { SbInputSelectComponent } from '../../../_components/unified/sb-select/sb-select.component';
import { SbRadioComponent } from '../../../_components/unified/sb-radio/sb-radio.component';
import { SbTextareaComponent } from '../../../_components/unified/sb-textarea/sb-textarea.component';

// While a "Run check now" is queued the page re-reads itself at this pace, so the result shows up
// without a manual refresh. A UI cadence only: the check itself is rate-limited by the server.
export const QUEUED_POLL_MS = 10000;
// Stop polling after this many reads; a check that takes longer is picked up on the next visit.
export const QUEUED_POLL_LIMIT = 30;
export const HISTORY_PER_PAGE = 20;
// Run check now opens again a moment after runNow.availableAt, so the server's clock has passed it too.
export const RUN_NOW_SLACK_MS = 1000;
// The longest delay setTimeout keeps; a later availableAt waits for the next load instead.
const MAX_TIMER_MS = 2147483647;

const LEVEL_LABELS: {[level in MonitoringLevel]: string} = {
  normal: 'Normal',
  warning: 'Warning',
  error: 'Error',
  paused: 'Paused',
  disabled: 'Disabled'
};

export const METRIC_ROWS: {key: keyof MonitoringMetrics; label: string}[] = [
  {key: 'loads', label: 'Widget loads'},
  {key: 'failedLoads', label: 'Failed widget loads'},
  {key: 'sessions', label: 'Widget sessions'},
  {key: 'shown', label: 'Panel shown'},
  {key: 'interactions', label: 'Interactions'},
  {key: 'opens', label: 'Opens'},
  {key: 'shares', label: 'Shares'},
  {key: 'registrations', label: 'Widget registrations'},
  {key: 'registerFailures', label: 'Registration failures'},
  {key: 'conversions', label: 'Conversions'}
];

const TRACKER_LABELS: {[tracker: string]: string} = {
  url: 'Referral link',
  on_site_cookie: 'On-site cookie',
  on_site_html5_storage: 'On-site browser storage',
  off_site_cookie: 'Off-site cookie',
  off_site_html5_storage: 'Off-site browser storage',
  client_fingerprint: 'Browser fingerprint',
  ip: 'IP address'
};

export const MUTE_OPTIONS: {id: MonitoringMute | 'default'; name: string}[] = [
  {id: 'default', name: 'Default period'},
  {id: 'week', name: 'A week'},
  {id: 'month', name: 'A month'},
  {id: 'forever', name: 'Forever'}
];

export interface ResolutionOption {
  value: MonitoringResolution;
  label: string;
  description: string;
}

/** The acknowledgement dialog's four choices. */
export const RESOLUTION_OPTIONS: ResolutionOption[] = [
  {value: 'fixed', label: 'Fixed',
    description: 'Closed. If the check finds the problem again, a new issue opens and emails.'},
  {value: 'acknowledge', label: 'Acknowledge',
    description: 'Stays open and keeps being checked, with no more emails for it. It closes by itself once the problem is gone.'},
  {value: 'ignore', label: 'Ignore',
    description: 'Closed, and this check is muted for the period you choose: still computed and shown, never emailed.'},
  {value: 'false_alarm', label: 'False alarm',
    description: 'Closed as an alarm the check should not have raised, and this check is muted for the period you choose. '
      + 'Kept apart from Ignore to fine-tune the checks.'}
];

// An acknowledged-open issue can still be marked as fixed, ignored or a false alarm, not acknowledged again.
export const CHANGE_OPTIONS: ResolutionOption[] = RESOLUTION_OPTIONS.filter((option) => option.value !== 'acknowledge');

// A manual issue is a person's report: closing it mutes nothing, so its choices say so.
const MANUAL_DESCRIPTIONS: {[resolution in MonitoringResolution]?: string} = {
  fixed: 'Closed.',
  ignore: 'Closed; nothing is muted.',
  false_alarm: 'Closed as not a real problem; nothing is muted.'
};

const forIssue = (options: ResolutionOption[], issue: MonitoringIssue | null): ResolutionOption[] =>
  issue?.type === 'manual'
    ? options.map((option) => ({...option, description: MANUAL_DESCRIPTIONS[option.value] || option.description}))
    : options;

/** The choices that mute the check for a period. */
export const MUTING_RESOLUTIONS: MonitoringResolution[] = ['ignore', 'false_alarm'];

const RESOLUTION_DONE: {[resolution in MonitoringResolution]: string} = {
  fixed: 'marked as fixed',
  acknowledge: 'acknowledged',
  ignore: 'ignored',
  false_alarm: 'marked as a false alarm'
};

/** The Log an issue dialog's levels. */
export const LOG_LEVEL_OPTIONS: {value: MonitoringIssueLevel; label: string; description: string}[] = [
  {value: 'error', label: 'Error', description: 'Something is broken: the widget or referrals do not work as they should.'},
  {value: 'warning', label: 'Warning', description: 'Something looks wrong and needs a look.'}
];

export function levelBadgeClass(level: MonitoringLevel | MonitoringIssueLevel | string): string {
  switch (level) {
    case 'normal':
      return 'sh-badge sh-badge--ok';
    case 'warning':
      return 'sh-badge sh-badge--warn';
    case 'error':
      return 'sh-badge sh-badge--error';
    default:
      return 'sh-badge sh-badge--unknown';
  }
}

export function levelLabel(level: MonitoringLevel | string): string {
  return LEVEL_LABELS[level as MonitoringLevel] || (level ? level.charAt(0).toUpperCase() + level.slice(1) : 'Unknown');
}

/** camelCase or snake_case key to a short English label ("shown7d" -> "Shown 7d"). */
export function humaniseKey(key: string): string {
  const words = String(key)
    .replace(/_/g, ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/([a-zA-Z])(\d)/g, '$1 $2')
    .toLowerCase()
    .trim();
  return words.charAt(0).toUpperCase() + words.slice(1);
}

/**
 * What the user typed or pasted, as a bare hostname: a pasted URL keeps only its host, a port or
 * path is dropped. Returns null when nothing hostname-shaped is left. The server validates again.
 */
export function normaliseHost(input: string): string | null {
  let value = (input || '').trim().toLowerCase();
  if (!value) {
    return null;
  }
  if (value.includes('://')) {
    try {
      value = new URL(value).hostname;
    } catch {
      return null;
    }
  }
  value = value.split(/[/?#]/)[0].replace(/:\d*$/, '').replace(/\.$/, '');
  if (value.length > 253 || !value.includes('.')) {
    return null;
  }
  return /^[a-z0-9]([a-z0-9-]*[a-z0-9])?(\.[a-z0-9]([a-z0-9-]*[a-z0-9])?)+$/.test(value) ? value : null;
}

export const ISSUE_LEVEL_FILTERS: {id: MonitoringIssueLevel | 'all'; name: string}[] = [
  {id: 'all', name: 'All levels'},
  {id: 'error', name: 'Errors'},
  {id: 'warning', name: 'Warnings'}
];

export const ISSUE_STATE_FILTERS: {id: MonitoringIssueState | 'all'; name: string}[] = [
  {id: 'all', name: 'All states'},
  {id: 'open', name: 'Open'},
  {id: 'acknowledged', name: 'Acknowledged'},
  {id: 'fixed', name: 'Fixed'},
  {id: 'ignored', name: 'Ignored'},
  {id: 'recovered', name: 'Recovered'},
  {id: 'false_alarm', name: 'False alarm'},
  {id: 'disabled', name: 'Closed when disabled'}
];

const ISSUE_STATE_LABELS: {[state in MonitoringIssueState]: string} = {
  open: 'Open',
  acknowledged: 'Acknowledged',
  fixed: 'Fixed',
  ignored: 'Ignored',
  recovered: 'Recovered',
  false_alarm: 'False alarm',
  disabled: 'Closed when disabled'
};

const CLOSE_REASON_LABELS: {[reason in MonitoringCloseReason]: string} = {
  recovered: 'Recovered',
  fixed: 'Fixed',
  ignored: 'Ignored',
  false_alarm: 'False alarm',
  disabled: 'Closed when disabled'
};

const HISTORY_TYPES: MonitoringHistoryType[] = ['issues', 'checks', 'events'];

type SiteAction = 'approve' | 'decline' | 'replace' | 'add';

@Component({
  selector: 'app-project-monitoring',
  templateUrl: './monitoring.component.html',
  styleUrls: ['./monitoring.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [PageShellComponent, SbButtonComponent, SbInputTextComponent, ReactiveFormsModule, FormsModule, NgTemplateOutlet, SbTabsComponent, SbTabDirective, SbInputSelectComponent, SbRadioComponent, SbTextareaComponent]
})
export class ProjectMonitoringComponent implements OnInit, OnDestroy {
  private route = inject(ActivatedRoute);
  private monitoringService = inject(ProjectMonitoringService);
  private sbModal = inject(SbModalService);
  private toastr = inject(ToastrService);
  // The page's state is many plain fields read through getters; every answer and timer below
  // marks the view for check.
  private cdr = inject(ChangeDetectorRef);

  readonly ackFormTpl = viewChild<TemplateRef<unknown>>('ackFormTpl');
  readonly siteFormTpl = viewChild<TemplateRef<unknown>>('siteFormTpl');
  readonly logIssueTpl = viewChild<TemplateRef<unknown>>('logIssueTpl');
  readonly disableTpl = viewChild<TemplateRef<unknown>>('disableTpl');

  /**
   * The project to show. Set, the page is embedded (Admin > Projects Monitoring opens it in a
   * dialog) and the route is never read; unset, the route's :projectId decides.
   */
  readonly projectIdInput = input<number | null>(null, {alias: 'projectId'});
  readonly projectId = linkedSignal(() => this.projectIdInput());
  // Given the project through the input: rendered without the page shell's top offset.
  embedded = false;
  data: ProjectMonitoringData | null = null;
  loading = false;
  error: string | null = null;
  // A write in flight; every action button waits for it.
  busy = false;

  openErrors: MonitoringIssue[] = [];
  openWarnings: MonitoringIssue[] = [];
  // Open but acknowledged: still checked, listed greyed under the open ones.
  acknowledgedIssues: MonitoringIssue[] = [];
  // Closed (fixed, ignored, false alarm or recovered), listed collapsed.
  settledIssues: MonitoringIssue[] = [];
  showSettled = false;
  showChecks = false;
  trackerKeys: string[] = [];

  addHost = '';
  addHostError = '';

  // The two dialogs' fields; the templates bind straight to these.
  ackForm: {resolution: MonitoringResolution; mute: MonitoringMute | 'default'; note: string} =
    {resolution: 'fixed', mute: 'default', note: ''};
  ackTarget: {issue: MonitoringIssue | null; level: MonitoringIssueLevel | null} = {issue: null, level: null};
  // The dialog's choices: all four, or all but Acknowledge for an acknowledged-open issue.
  ackOptions: ResolutionOption[] = RESOLUTION_OPTIONS;
  // The Log an issue dialog: the level and what was seen; Log stays off until there is text.
  logForm: {level: MonitoringIssueLevel; description: string} = {level: 'warning', description: ''};
  logButtons: SbModalButton[] = [];
  readonly logLevelOptions = LOG_LEVEL_OPTIONS;
  // The Disable monitoring dialog: why (optional).
  disableForm: {note: string} = {note: ''};
  siteForm: {action: SiteAction; site: MonitoringSite | null; host: string; note: string; hostError: string} =
    {action: 'approve', site: null, host: '', note: '', hostError: ''};

  siteButtons: SbModalButton[] = [];

  historyType: MonitoringHistoryType = 'issues';
  historyPage = 1;
  history: MonitoringHistoryPage<MonitoringIssueHistoryRow | MonitoringCheckRow | MonitoringEventRow> | null = null;
  // History > Issues filters; 'all' sends none.
  issueLevelFilter: MonitoringIssueLevel | 'all' = 'all';
  issueStateFilter: MonitoringIssueState | 'all' = 'all';
  historyLoading = false;
  historyError: string | null = null;

  readonly metricRows = METRIC_ROWS;
  readonly muteOptions = MUTE_OPTIONS;
  readonly issueLevelFilters = ISSUE_LEVEL_FILTERS;
  readonly issueStateFilters = ISSUE_STATE_FILTERS;
  readonly levelBadgeClass = levelBadgeClass;
  readonly levelLabel = levelLabel;
  readonly humaniseKey = humaniseKey;

  private request: Subscription | null = null;
  private historyRequest: Subscription | null = null;
  private writeRequest: Subscription | null = null;
  private routeSub: Subscription | null = null;
  private pollTimer: ReturnType<typeof setTimeout> | null = null;
  private runNowTimer: ReturnType<typeof setTimeout> | null = null;
  private pollCount = 0;
  private destroyed = false;
  // The project the page holds; the input or the route may name another one next.
  private openedProjectId: number | null = null;

  constructor() {
    // Embedded, another project given starts the page over (ngOnInit opened the first one).
    effect(() => {
      const projectId = this.projectIdInput();
      untracked(() => {
        if (this.embedded && projectId && Number(projectId) !== this.openedProjectId) {
          this.openProject(Number(projectId));
        }
      });
    });
  }

  ngOnInit() {
    const projectId = this.projectId();
    if (projectId) {
      this.embedded = true;
      this.openProject(Number(projectId));
      return;
    }
    // The project switcher keeps this page mounted and only changes :projectId, so every new id
    // starts the page over rather than leaving the last project's data (and its writes) behind.
    this.routeSub = this.route.paramMap.subscribe((params) => {
      this.openProject(Number(params.get('projectId')));
      this.cdr.markForCheck();
    });
  }

  ngOnDestroy() {
    this.destroyed = true;
    this.routeSub?.unsubscribe();
    this.stopProject();
  }

  get manage(): boolean {
    return !!this.data?.permissions?.manage;
  }

  /**
   * Active, Paused or Disabled; an older payload without `state` is active or paused by `active`.
   */
  get monitoringState(): MonitoringState {
    const monitoring = this.data?.monitoring;
    if (monitoring?.state) {
      return monitoring.state;
    }
    return monitoring?.active ? 'active' : 'paused';
  }

  /** "Disabled by <name> on <date>: <note>" for a person's decision; null otherwise. */
  get disabledByText(): string | null {
    const disabled = this.data?.monitoring?.disabled;
    if (!disabled) {
      return null;
    }
    const by = disabled.by?.name ? ` by ${disabled.by.name}` : '';
    const on = disabled.at ? ` on ${this.time(disabled.at)}` : '';
    return `Disabled${by}${on}${disabled.note ? `: ${disabled.note}` : ''}`;
  }

  /** A pending site's audience line: "Seen from 1 browser on 3 days: not asked about yet". */
  audienceText(site: MonitoringSite): string | null {
    const audience = site.audience;
    if (!audience || !audience.lowAudience) {
      return null;
    }
    const browsers = audience.browsers === 1 ? '1 browser' : `${audience.browsers} browsers`;
    const days = audience.days === 1 ? '1 day' : `${audience.days} days`;
    return `Seen from ${browsers} on ${days} (a preview, a staging copy or one tester?): nobody is asked about it yet.`;
  }

  get timezone(): string {
    return this.data?.project?.timezone || this.viewerTimezone();
  }

  get pendingSites(): MonitoringSite[] {
    return (this.data?.sites || []).filter((site) => site.status === 'pending');
  }

  /** Every pending site, listed or not: the server's count when it sends one, else the listed ones. */
  get pendingCount(): number {
    const pending = this.data?.sitesSummary?.pending;
    return typeof pending === 'number' ? pending : this.pendingSites.length;
  }

  /** "N more sites not shown" when the list is cut, else null. */
  get sitesNotShownText(): string | null {
    const count = this.data?.sitesSummary?.notListed || 0;
    if (count <= 0) {
      return null;
    }
    return count === 1 ? '1 more site not shown.' : `${count} more sites not shown.`;
  }

  /** The new sites the latest check held back from proposing, while too many were pending; else null. */
  get sitesHeldBackText(): string | null {
    const count = this.data?.sitesSummary?.heldBack || 0;
    if (count <= 0) {
      return null;
    }
    return `${count === 1 ? '1 new site is' : count + ' new sites are'} waiting to be proposed: decide on the pending ones first.`;
  }

  /**
   * The pending sites whose widget loads count as unexpected until someone approves them: those
   * seen only through loads and proposed after the first check settled the sites (the server's
   * countsAsUnexpected).
   */
  get pendingUnexpectedSites(): MonitoringSite[] {
    return this.pendingSites.filter((site) => this.siteCountsAsUnexpected(site));
  }

  /**
   * Whether a site's widget loads count as unexpected: the server's word; for an older payload
   * without it, the rule that server judged by (declined, or pending and seen only through loads).
   */
  siteCountsAsUnexpected(site: MonitoringSite): boolean {
    if (typeof site.countsAsUnexpected === 'boolean') {
      return site.countsAsUnexpected;
    }
    return site.status === 'declined' || (site.status === 'pending' && !this.siteExpected(site));
  }

  /** The server's word on whether a site counts as expected; the same rule for a payload without it. */
  siteExpected(site: MonitoringSite): boolean {
    if (typeof site.expected === 'boolean') {
      return site.expected;
    }
    return site.status === 'approved' || (site.status === 'pending' && site.source === 'registration');
  }

  get runNowBlocked(): boolean {
    const runNow = this.data?.monitoring?.runNow;
    if (!runNow) {
      return true;
    }
    if (runNow.queued) {
      return true;
    }
    return !!runNow.availableAt && Date.parse(runNow.availableAt) > Date.now();
  }

  get runNowHint(): string | null {
    const runNow = this.data?.monitoring?.runNow;
    if (!runNow) {
      return null;
    }
    if (runNow.queued) {
      return 'A check is queued and runs shortly; this page updates when it is done.';
    }
    if (runNow.availableAt && Date.parse(runNow.availableAt) > Date.now()) {
      return `The next manual check is available at ${this.time(runNow.availableAt)}.`;
    }
    return null;
  }

  get unavailableChecks(): MonitoringCheckRule[] {
    return (this.checkRules).filter((check) => !check.available);
  }

  get historyTotalPages(): number {
    if (!this.history) {
      return 1;
    }
    return Math.max(1, Math.ceil(this.history.total / (this.history.perPage || HISTORY_PER_PAGE)));
  }

  get checkRows(): MonitoringCheckRow[] {
    return this.historyType === 'checks' ? (this.history?.rows || []) as MonitoringCheckRow[] : [];
  }

  get eventRows(): MonitoringEventRow[] {
    return this.historyType === 'events' ? (this.history?.rows || []) as MonitoringEventRow[] : [];
  }

  get issueRows(): MonitoringIssueHistoryRow[] {
    return this.historyType === 'issues' ? (this.history?.rows || []) as MonitoringIssueHistoryRow[] : [];
  }

  get issueFiltered(): boolean {
    return this.issueLevelFilter !== 'all' || this.issueStateFilter !== 'all';
  }

  load() {
    this.request?.unsubscribe();
    this.loading = true;
    this.request = this.monitoringService.get(this.projectId()).subscribe({
      next: (res) => {
        this.loading = false;
        if (!this.apply(res)) {
          this.error = 'The monitoring answer was not understood.';
        }
        this.cdr.markForCheck();
      },
      error: (err) => {
        this.loading = false;
        this.error = this.messageOf(err, 'Failed to load monitoring. The API may be unreachable.');
        this.cdr.markForCheck();
      }
    });
  }

  /** Replaces the page state with a full payload (the GET, and every write's answer). */
  apply(res: MonitoringResponse<ProjectMonitoringData> | null): boolean {
    const data = res?.data;
    if (!data || !data.monitoring || !data.status) {
      return false;
    }
    const wasQueued = !!this.data?.monitoring?.runNow?.queued;
    this.data = data;
    this.error = null;
    this.regroup();
    this.schedulePoll();
    this.scheduleRunNowOpen();
    // A queued check has just finished: its row (and what it found) is new history, on every tab
    // (Events gets its check event, and the issue, site and email events it wrote).
    if (wasQueued && !data.monitoring.runNow?.queued) {
      this.loadHistory();
    }
    return true;
  }

  // --- history ---

  onHistoryTab(type: string) {
    if (!HISTORY_TYPES.includes(type as MonitoringHistoryType)) {
      return;
    }
    if (type === this.historyType && this.history) {
      return;
    }
    this.historyType = type as MonitoringHistoryType;
    this.historyPage = 1;
    this.history = null;
    this.loadHistory();
  }

  /** The History > Issues filters as the API takes them: a filter left on "all" is not sent. */
  issueFilters(): MonitoringIssueHistoryFilters {
    const filters: MonitoringIssueHistoryFilters = {};
    if (this.issueLevelFilter !== 'all') {
      filters.level = this.issueLevelFilter;
    }
    if (this.issueStateFilter !== 'all') {
      filters.state = this.issueStateFilter;
    }
    return filters;
  }

  /** A level or state filter of History > Issues changed: back to the first page. */
  onIssueFilterChange() {
    if (this.historyType !== 'issues') {
      return;
    }
    this.historyPage = 1;
    this.history = null;
    this.loadHistory();
  }

  historyGo(delta: number) {
    const page = this.historyPage + delta;
    if (page < 1 || page > this.historyTotalPages) {
      return;
    }
    this.historyPage = page;
    this.loadHistory();
  }

  loadHistory() {
    this.historyRequest?.unsubscribe();
    this.historyLoading = true;
    const type = this.historyType;
    const request = type === 'issues'
      ? this.monitoringService.issueHistory(this.projectId(), this.issueFilters(),
        this.historyPage, HISTORY_PER_PAGE)
      : type === 'checks'
        ? this.monitoringService.history(this.projectId(), 'checks', this.historyPage, HISTORY_PER_PAGE)
        : this.monitoringService.history(this.projectId(), 'events', this.historyPage, HISTORY_PER_PAGE);
    this.historyRequest = (request as Observable<MonitoringResponse<MonitoringHistoryPage<any>>>).subscribe({
      next: (res) => {
        this.historyLoading = false;
        this.cdr.markForCheck();
        if (type !== this.historyType) {
          return;
        }
        if (!res?.data || !Array.isArray(res.data.rows)) {
          this.historyError = 'The history answer was not understood.';
          return;
        }
        this.history = res.data;
        this.historyError = null;
      },
      error: (err) => {
        this.historyLoading = false;
        this.historyError = this.messageOf(err, 'Failed to load the history.');
        this.cdr.markForCheck();
      }
    });
  }

  // --- run now ---

  runNow() {
    if (!this.manage || this.busy || this.runNowBlocked || this.monitoringState === 'disabled') {
      return;
    }
    this.write(this.monitoringService.runNow(this.projectId()), 'Check queued. This page updates when it is done.');
  }

  // --- disable / enable ---

  /** Opens the Disable monitoring dialog (a test or demo project, say): why, optional. */
  disableMonitoring() {
    if (!this.manage || this.busy || this.data?.monitoring?.disabled) {
      return;
    }
    this.disableForm = {note: ''};
    const buttons: SbModalButton[] = [
      {id: 'cancel', label: 'Cancel', style: 'default', dismiss: true},
      {id: 'confirm', label: 'Disable monitoring', style: 'danger', closeValue: true}
    ];
    this.sbModal.open({title: 'Disable monitoring', bodyTemplate: this.disableTpl(), size: 'md', scrollable: false,
      buttons}).result.then(() => this.submitDisable(), () => undefined);
  }

  submitDisable() {
    this.write(this.monitoringService.disable(this.projectId(), this.disableForm.note), 'Monitoring disabled');
  }

  /** Lifts a person's decision to disable monitoring. */
  enableMonitoring() {
    if (!this.manage || this.busy || !this.data?.monitoring?.disabled) {
      return;
    }
    this.write(this.monitoringService.enable(this.projectId()), 'Monitoring enabled');
  }

  // --- issues ---

  /** Opens the acknowledgement dialog for one open issue: Fixed, Acknowledge or Ignore. */
  acknowledge(issue: MonitoringIssue) {
    if (!this.manage || this.busy) {
      return;
    }
    this.openAckDialog({issue, level: null}, RESOLUTION_OPTIONS, `Acknowledge "${issue.title}"`);
  }

  /** Everything a check looks at: the integration's rules, then the call to action links. */
  get checkRules(): MonitoringCheckRule[] {
    return [...(this.data?.checks || []), ...(this.data?.monitoring?.linkChecks || [])];
  }

  /**
   * A call to action link finding asks one thing: the links it names work as expected. Saying so
   * closes it; it is raised again only for a link nobody has confirmed.
   */
  confirmLinks(issue: MonitoringIssue) {
    if (!this.manage || this.busy) {
      return;
    }
    this.write(this.monitoringService.acknowledgeIssue(this.projectId(), issue.id, {resolution: 'acknowledge'}),
      'Confirmed: the warning returns only if a link changes', undefined, true);
  }

  /** Changes the choice for an acknowledged-open issue: Fixed, Ignore or False alarm. */
  changeAcknowledgement(issue: MonitoringIssue) {
    if (!this.manage || this.busy) {
      return;
    }
    this.openAckDialog({issue, level: null}, CHANGE_OPTIONS, `Change "${issue.title}"`);
  }

  /** The same dialog, for every unacknowledged open issue of a level. */
  acknowledgeAll(level: MonitoringIssueLevel) {
    if (!this.manage || this.busy) {
      return;
    }
    this.openAckDialog({issue: null, level}, RESOLUTION_OPTIONS, `Acknowledge all open ${this.levelNoun(level)}`);
  }

  submitAcknowledgement() {
    const {resolution, mute, note} = this.ackForm;
    // Only a choice the dialog offered goes: Acknowledge again is not one for an acknowledged issue.
    if (!this.ackOptions.some((option) => option.value === resolution)) {
      return;
    }
    const body = {
      resolution,
      mute: this.muteOffered && mute !== 'default' ? mute : undefined,
      note
    };
    const {issue, level} = this.ackTarget;
    // A refused acknowledgement (400) usually means the page is behind: a check closed the issue,
    // or somebody else acknowledged it first. The page is reloaded so it stops offering it.
    if (issue) {
      this.write(this.monitoringService.acknowledgeIssue(this.projectId(), issue.id, body), `Issue ${RESOLUTION_DONE[resolution]}`,
        undefined, true);
    } else if (level) {
      this.write(this.monitoringService.acknowledgeLevel(this.projectId(), level, body),
        `All open ${this.levelNoun(level)} ${RESOLUTION_DONE[resolution]}`, undefined, true);
    }
  }

  /** Whether the dialog asks for a mute period: Ignore or False alarm, never for a manual issue. */
  get muteOffered(): boolean {
    return MUTING_RESOLUTIONS.includes(this.ackForm.resolution) && this.ackTarget.issue?.type !== 'manual';
  }

  /** Opens the Log an issue dialog: what was seen, as an error or a warning. */
  logIssue() {
    if (!this.manage || this.busy) {
      return;
    }
    this.logForm = {level: 'warning', description: ''};
    this.logButtons = [
      {id: 'cancel', label: 'Cancel', style: 'default', dismiss: true},
      {id: 'confirm', label: 'Log issue', style: 'primary', closeValue: true, disabled: true}
    ];
    this.sbModal.open({title: 'Log an issue', bodyTemplate: this.logIssueTpl(), size: 'md', scrollable: false,
      buttons: this.logButtons}).result.then(() => this.submitLogIssue(), () => undefined);
  }

  onLogDescriptionChange(value: string) {
    this.logForm.description = value;
    if (this.logButtons[1]) {
      this.logButtons[1].disabled = !(value || '').trim();
    }
  }

  submitLogIssue(): boolean {
    const description = (this.logForm.description || '').trim();
    if (!description) {
      return false;
    }
    this.write(this.monitoringService.logIssue(this.projectId(), {level: this.logForm.level, description}),
      `${this.logForm.level === 'error' ? 'Error' : 'Warning'} logged`);
    return true;
  }

  /** "Logged by <name> on <date>" for an issue a person logged; null for a check's. */
  reportedText(issue: MonitoringIssue): string | null {
    if (issue.type !== 'manual') {
      return null;
    }
    const by = issue.reportedBy?.name ? ` by ${issue.reportedBy.name}` : '';
    return `Logged${by} on ${this.time(issue.openedAt)}`;
  }

  /** Who acknowledged an issue and when: "by <name> on <date>", each part only when known. */
  acknowledgedByText(issue: MonitoringIssue): string {
    const by = issue.acknowledgedBy?.name ? ` by ${issue.acknowledgedBy.name}` : '';
    const on = issue.acknowledgedAt ? ` on ${this.time(issue.acknowledgedAt)}` : '';
    return `${by}${on}`.trim();
  }

  /** The line an acknowledged-open issue carries: "Acknowledged by <name> on <date>". */
  acknowledgedText(issue: MonitoringIssue): string {
    const who = this.acknowledgedByText(issue);
    return who ? `Acknowledged ${who}` : 'Acknowledged';
  }

  issueStateText(issue: MonitoringIssue): string {
    const who = this.acknowledgedByText(issue);
    const closed = issue.closedAt ? ` on ${this.time(issue.closedAt)}` : '';
    switch (issue.state) {
      case 'acknowledged':
        return this.acknowledgedText(issue);
      case 'fixed':
        return `Fixed${who ? ` (${who})` : closed}`;
      case 'ignored':
        return `Ignored${who ? ` (${who})` : closed}${this.ignoredMuteText(issue.mutedUntil)}`;
      case 'false_alarm':
        return `False alarm${who ? ` (${who})` : closed}${issue.mutedUntil ? this.ignoredMuteText(issue.mutedUntil) : ''}`;
      case 'recovered':
        // An acknowledged-open issue that recovered still says who acknowledged it.
        return `Recovered${closed}${issue.acknowledgedAt ? ` (acknowledged ${who})` : ''}`;
      default:
        return 'Open';
    }
  }

  issueStateLabel(state: MonitoringIssueState | string): string {
    return ISSUE_STATE_LABELS[state as MonitoringIssueState] || humaniseKey(String(state || 'unknown'));
  }

  /** How a closed issue was closed: recovered by itself, fixed, or ignored. */
  closeReasonLabel(reason: string | null): string {
    if (!reason) {
      return '';
    }
    return CLOSE_REASON_LABELS[reason] || humaniseKey(reason);
  }

  /** History > Issues' "Muted until" cell: a date, "Forever", or a dash when not muted. */
  mutedUntilText(mutedUntil: string | null): string {
    if (!mutedUntil) {
      return '—';
    }
    return this.muteText(mutedUntil) === 'forever' ? 'Forever' : this.date(mutedUntil);
  }

  /** How many checks found an issue, in words. */
  seenText(count: number | null | undefined): string {
    const value = Number(count);
    if (!Number.isFinite(value) || value < 1) {
      return '—';
    }
    return value === 1 ? 'Once' : `${value.toLocaleString()} times`;
  }

  muteText(mutedUntil: string | null): string {
    if (!mutedUntil) {
      return '';
    }
    return moment.utc(mutedUntil).year() >= 9999 ? 'forever' : `until ${this.date(mutedUntil)}`;
  }

  /** The evidence as the server words it (evidenceItems); the raw keys only when a payload has none. */
  evidenceEntries(issue: MonitoringIssue): {key: string; value: string}[] {
    if (Array.isArray(issue.evidenceItems)) {
      return issue.evidenceItems.map((item) => ({key: item.label, value: item.value}));
    }
    return Object.entries(issue.evidence || {}).map(([key, value]) => ({key: humaniseKey(key), value: this.valueText(value)}));
  }

  thresholdEntries(check: MonitoringCheckRule): {key: string; value: string}[] {
    return Object.entries(check.thresholds || {}).map(([key, value]) => ({key: humaniseKey(key), value: this.valueText(value)}));
  }

  checksOfLevel(level: MonitoringIssueLevel | string): MonitoringCheckRule[] {
    return (this.checkRules).filter((check) => check.level === level);
  }

  // --- sites ---

  approveSite(site: MonitoringSite) {
    this.openSiteDialog('approve', site, `Approve ${site.host}`, 'Approve');
  }

  declineSite(site: MonitoringSite) {
    this.openSiteDialog('decline', site, `Decline ${site.host}`, 'Decline');
  }

  replaceSite(site: MonitoringSite) {
    this.openSiteDialog('replace', site, `Replace ${site.host}`, 'Replace');
  }

  addSite() {
    if (!this.manage || this.busy) {
      return;
    }
    const host = normaliseHost(this.addHost);
    if (!host) {
      this.addHostError = 'Enter a hostname such as www.example.com.';
      return;
    }
    this.addHostError = '';
    this.write(this.monitoringService.addSite(this.projectId(), host), `${host} added as an approved site`, () => this.addHost = '');
  }

  submitSiteDecision(): boolean {
    const {action, site, note} = this.siteForm;
    if (!site) {
      return false;
    }
    if (action === 'approve') {
      this.write(this.monitoringService.approveSite(this.projectId(), site.id, note), `${site.host} approved`);
    } else if (action === 'decline') {
      this.write(this.monitoringService.declineSite(this.projectId(), site.id, note), `${site.host} declined`);
    } else if (action === 'replace') {
      const host = normaliseHost(this.siteForm.host);
      if (!host) {
        this.toastr.error('Enter a hostname such as www.example.com.');
        return false;
      }
      this.write(this.monitoringService.replaceSite(this.projectId(), site.id, host, note), `${site.host} replaced by ${host}`);
    }
    return true;
  }

  onReplaceHostChange(value: string) {
    this.siteForm.host = value;
    const valid = !!normaliseHost(value);
    this.siteForm.hostError = value && !valid ? 'Enter a hostname such as www.example.com.' : '';
    if (this.siteButtons[1]) {
      this.siteButtons[1].disabled = !valid;
    }
  }

  siteSourceText(site: MonitoringSite): string {
    switch (site.source) {
      case 'registration':
        return 'Proposed by a registration through the widget on this site';
      case 'load':
        return 'Proposed because the widget was loaded on this site';
      default:
        return 'Added by hand';
    }
  }

  siteChipClass(status: string | null): string {
    return 'pm-chip' + (status ? ' pm-chip--' + status : '');
  }

  // --- metrics ---

  metricText(value: number | null | undefined): string {
    return value === null || value === undefined ? 'n/a' : Number(value).toLocaleString();
  }

  trackerLabel(key: string): string {
    return TRACKER_LABELS[key] || humaniseKey(key);
  }

  /** A conversions-by-tracker row's label; a conversion with no tracker (`unknown`) says so. */
  trackerRowLabel(key: string): string {
    if (!key || key === 'unknown' || key === 'null') {
      return 'Conversions with no tracker recorded';
    }
    return `Conversions by ${this.trackerLabel(key)}`;
  }

  /** The window the loads-by-host list covers, as the heading names it ("last 7 days"). */
  windowText(hours: number | null | undefined): string {
    const value = Number(hours);
    if (!Number.isFinite(value) || value <= 0) {
      return '';
    }
    if (value % 24 === 0 && value > 24) {
      return `last ${value / 24} days`;
    }
    return `last ${value} hours`;
  }

  trackerValue(window: MonitoringMetrics | null | undefined, key: string): number | null {
    const trackers = window?.conversionsByTracker;
    if (!trackers) {
      return null;
    }
    return trackers[key] ?? 0;
  }

  // --- formatting ---

  /** An API time (ISO UTC) in the project's timezone. */
  time(at: string | null | undefined): string {
    if (!at) {
      return '—';
    }
    const m = moment.utc(at);
    return m.isValid() ? m.tz(this.timezone).format('DD MMM YYYY, HH:mm') : '—';
  }

  date(at: string | null | undefined): string {
    if (!at) {
      return '—';
    }
    const m = moment.utc(at);
    return m.isValid() ? m.tz(this.timezone).format('DD MMM YYYY') : '—';
  }

  durationText(ms: number | null | undefined): string {
    if (ms === null || ms === undefined) {
      return '—';
    }
    return ms < 1000 ? `${ms} ms` : `${(ms / 1000).toFixed(1)} s`;
  }

  trackById(index: number, row: {id: number}) {
    return row.id;
  }

  trackByKey(index: number, row: {key: string}) {
    return row.key;
  }

  // --- internals ---

  private regroup() {
    const open = this.data?.issues?.open || [];
    const recent = this.data?.issues?.recent || [];
    const actionable = open.filter((issue) => issue.state === 'open');
    this.openErrors = actionable.filter((issue) => issue.level === 'error');
    this.openWarnings = actionable.filter((issue) => issue.level === 'warning');
    // Acknowledged and still open: greyed under the open ones, in the server's order.
    this.acknowledgedIssues = open.filter((issue) => issue.state === 'acknowledged');
    // Closed (fixed, ignored, recovered), and anything else the open list should not hold, collapsed.
    this.settledIssues = [...open.filter((issue) => issue.state !== 'open' && issue.state !== 'acknowledged'), ...recent];
    const windows = this.data?.metrics?.windows;
    const keys = new Set<string>();
    [windows?.day, windows?.week, windows?.previousWeek].forEach((window) =>
      Object.keys(window?.conversionsByTracker || {}).forEach((key) => keys.add(key)));
    this.trackerKeys = Array.from(keys);
  }

  private openAckDialog(target: {issue: MonitoringIssue | null; level: MonitoringIssueLevel | null},
    options: ResolutionOption[], title: string) {
    this.ackTarget = target;
    this.ackOptions = forIssue(options, target.issue);
    // Acknowledge is picked when offered (the button says so); a change starts on Fixed.
    const resolution = options.some((option) => option.value === 'acknowledge') ? 'acknowledge' : options[0].value;
    this.ackForm = {resolution, mute: 'default', note: ''};
    const buttons: SbModalButton[] = [
      {id: 'cancel', label: 'Cancel', style: 'default', dismiss: true},
      {id: 'confirm', label: 'Save', style: 'primary', closeValue: true}
    ];
    this.sbModal.open({title, bodyTemplate: this.ackFormTpl(), size: 'md', scrollable: false, buttons})
      .result.then(() => this.submitAcknowledgement(), () => undefined);
  }

  private openSiteDialog(action: SiteAction, site: MonitoringSite, title: string, confirmLabel: string) {
    if (!this.manage || this.busy) {
      return;
    }
    this.siteForm = {action, site, host: '', note: '', hostError: ''};
    // Replace stays off until a valid host is typed; the dialog reads this array live.
    this.siteButtons = [
      {id: 'cancel', label: 'Cancel', style: 'default', dismiss: true},
      {id: 'confirm', label: confirmLabel, style: action === 'decline' ? 'danger' : 'primary', closeValue: true,
        disabled: action === 'replace'}
    ];
    this.sbModal.open({title, bodyTemplate: this.siteFormTpl(), size: 'md', scrollable: false, buttons: this.siteButtons})
      .result.then(() => this.submitSiteDecision(), () => undefined);
  }

  /** `reloadOnRefusal`: also reload on a 400, for a write the server refuses when the page is behind it. */
  private write(request: Observable<MonitoringResponse<ProjectMonitoringData>>, success: string, done?: () => void,
    reloadOnRefusal = false) {
    this.busy = true;
    this.writeRequest?.unsubscribe();
    this.writeRequest = request.subscribe({
      next: (res) => {
        this.busy = false;
        this.cdr.markForCheck();
        if (!this.apply(res)) {
          this.toastr.error('The answer was not understood; the page is reloaded.');
          this.load();
          return;
        }
        done?.();
        this.toastr.success(success);
        // A decision or an acknowledgement is a new event, and may change an issue's row.
        if (this.historyType !== 'checks') {
          this.loadHistory();
        }
      },
      error: (err) => {
        this.busy = false;
        this.cdr.markForCheck();
        this.toastr.error(this.messageOf(err, 'The change could not be saved.'));
        // A 404 or 409 means the page is behind the server (a site gone, a check already queued).
        if (err?.status === 404 || err?.status === 409 || (reloadOnRefusal && err?.status === 400)) {
          this.load();
        }
      }
    });
  }

  private schedulePoll() {
    this.clearPoll();
    if (this.destroyed || !this.data?.monitoring?.runNow?.queued) {
      this.pollCount = 0;
      return;
    }
    if (this.pollCount >= QUEUED_POLL_LIMIT) {
      return;
    }
    this.pollTimer = setTimeout(() => {
      this.pollCount += 1;
      this.load();
    }, QUEUED_POLL_MS);
  }

  private clearPoll() {
    if (this.pollTimer) {
      clearTimeout(this.pollTimer);
    }
    this.pollTimer = null;
  }

  /**
   * Run check now waits for runNow.availableAt, which the getters compare with the clock; nothing
   * else re-renders the page when that moment passes, so a one-shot timer does. While a check is
   * still queued at that moment (it failed, was lost, or outlasted the polling), the server has
   * given up on it too: the timer reads the page once more, so the button and the hint follow.
   */
  private scheduleRunNowOpen() {
    this.clearRunNowTimer();
    const runNow = this.data?.monitoring?.runNow;
    if (this.destroyed || !runNow || !runNow.availableAt) {
      return;
    }
    const wait = Date.parse(runNow.availableAt) - Date.now();
    if (!Number.isFinite(wait) || wait <= 0 || wait + RUN_NOW_SLACK_MS > MAX_TIMER_MS) {
      return;
    }
    const reload = !!runNow.queued;
    // The timer marks the view, so the getters are read again; the field only says it fired.
    this.runNowTimer = setTimeout(() => {
      this.runNowTimer = null;
      this.cdr.markForCheck();
      if (reload && !this.destroyed) {
        this.load();
      }
    }, wait + RUN_NOW_SLACK_MS);
  }

  private clearRunNowTimer() {
    if (this.runNowTimer) {
      clearTimeout(this.runNowTimer);
    }
    this.runNowTimer = null;
  }

  /** Opens the page for a project: everything of the one before is dropped first. */
  private openProject(projectId: number) {
    if (projectId === this.openedProjectId && (this.data || this.loading)) {
      return;
    }
    this.stopProject();
    this.openedProjectId = projectId;
    this.projectId.set(projectId);
    this.data = null;
    this.loading = false;
    this.error = null;
    this.busy = false;
    this.pollCount = 0;
    this.openErrors = [];
    this.openWarnings = [];
    this.acknowledgedIssues = [];
    this.settledIssues = [];
    this.trackerKeys = [];
    this.showSettled = false;
    this.addHost = '';
    this.addHostError = '';
    this.historyType = 'issues';
    this.historyPage = 1;
    this.history = null;
    this.issueLevelFilter = 'all';
    this.issueStateFilter = 'all';
    this.historyLoading = false;
    this.historyError = null;
    this.load();
    this.loadHistory();
  }

  /** Stops every timer and request of the project on the page. */
  private stopProject() {
    this.clearPoll();
    this.clearRunNowTimer();
    this.request?.unsubscribe();
    this.historyRequest?.unsubscribe();
    this.writeRequest?.unsubscribe();
    this.request = null;
    this.historyRequest = null;
    this.writeRequest = null;
  }

  private levelNoun(level: MonitoringIssueLevel): string {
    return level === 'error' ? 'errors' : 'warnings';
  }

  /** An ignored issue's mute: still lasting (forever, or until a date), or over. */
  private ignoredMuteText(mutedUntil: string | null): string {
    if (!mutedUntil) {
      return '';
    }
    const until = Date.parse(mutedUntil);
    if (Number.isFinite(until) && until <= Date.now()) {
      return `, mute ended ${this.date(mutedUntil)}`;
    }
    return `, muted ${this.muteText(mutedUntil)}`;
  }

  private valueText(value: unknown): string {
    if (value === null || value === undefined) {
      return 'n/a';
    }
    if (typeof value === 'number') {
      return value.toLocaleString();
    }
    if (typeof value === 'boolean') {
      return value ? 'yes' : 'no';
    }
    if (typeof value === 'object') {
      return JSON.stringify(value);
    }
    return String(value);
  }

  private messageOf(err: any, fallback: string): string {
    return err?.error?.message || fallback;
  }

  private viewerTimezone(): string {
    try {
      return JSON.parse(localStorage.getItem('currentUser') || 'null')?.timezone || 'UTC';
    } catch {
      return 'UTC';
    }
  }
}
